(ns ui.global-state
  (:require ["jquery"     :as jquerying]
            [hoplon.core  :as h :refer [do! do-watch]]
            [clojure.data :refer [diff]]
            [clojure.pprint :refer [pprint]]
            [clojure.string :as str]
            [ui.util :as util]
            [javelin.core :refer (cell cell= defc defc= with-let formula-of)]))

(def HOSTNAME (-> js/window .-location .-hostname))

(defn- try-match [pattern hostname]
  (-> pattern
      (re-matches hostname)
      second))

(defn- try-match-gcp-dev-zone [hostname]
  (try-match #".+\.([^.]+)\.management\.gcp\.dev\.opzerk\.com" hostname))

(defn- try-match-gcp-prod-zone [hostname]
  (or (try-match #".+\.([^.]+)\.management\.gcp\.prod\.opzerk\.com" hostname)
      (re-matches #"app\.gcp\.kevel\.co" hostname)))

(defn- try-match-aws-dev-zone [hostname]
  (try-match #".+\.([^.]+)\.dev\.opzerk\.com" hostname))

(defn- try-match-aws-prod-zone [hostname]
  (or (try-match #".+\.([^.]+)\.prod\.opzerk\.com" hostname)
      (re-matches #"app\.kevel\.co" hostname)))

(defn- try-match-jha-zone [hostname]
  (try-match #".+\.([^.]+)\.opzerk\.com" hostname))

;; Used to signal to the rest of the application code whether we are running it
;; in development mode, in a non-production deployment, or in production.
;; Creative Images will be a broken image if you are running Geir locally pointed at
;; mdev.
;;
;; IMPORTANT: The order of pattern checks below matters. More specific patterns must
;; be checked before more general ones:
;;   - gcp-dev-zone (*.management.gcp.dev.opzerk.com) before aws-dev-zone (*.dev.opzerk.com)
;;   - gcp-prod-zone (*.management.gcp.prod.opzerk.com) before aws-prod-zone (*.prod.opzerk.com)
;;   - aws-dev-zone (*.dev.opzerk.com) before jha-zone (*.opzerk.com)
;;   - aws-prod-zone (*.prod.opzerk.com) before jha-zone (*.opzerk.com)
(def GEIR_ENVIRONMENT
  (let [gcp-dev-zone  (try-match-gcp-dev-zone HOSTNAME)
        gcp-prod-zone (try-match-gcp-prod-zone HOSTNAME)
        aws-dev-zone  (try-match-aws-dev-zone HOSTNAME)
        aws-prod-zone (try-match-aws-prod-zone HOSTNAME)
        jha-zone      (try-match-jha-zone HOSTNAME)]
    (cond
      gcp-dev-zone             :gcp-dev
      gcp-prod-zone            :gcp-prod
      aws-dev-zone             :aws-dev
      aws-prod-zone            :aws-prod
      jha-zone                 :aws-jha
      ;; Tailscale MagicDNS hosts (e.g. minikevel served over a tailnet HTTPS
      ;; proxy) behave like a local dev stack, so classify them as :local. This
      ;; keeps GEIR_ENVIRONMENT non-nil, which several consumers rely on (e.g.
      ;; `(name GEIR_ENVIRONMENT)` in integrations.cljs would otherwise throw
      ;; post-login).
      (str/ends-with? HOSTNAME ".ts.net") :local
      (= "localhost" HOSTNAME) :local)))

(when (nil? GEIR_ENVIRONMENT)
  (js/console.warn "Unable to determine the environment based on hostname."
                   #js {:hostname HOSTNAME}))

;; Source of truth for the creative static-asset domain, supplied by the backend
;; (required KEVEL_GEIR_STATIC_ASSET_DOMAIN) and populated at boot by
;; `fetch-static-asset-domain!`. Starts nil until the boot get-env fetch resolves;
;; consumers are reactive (CLJS via `cell=`, TS via `mount-react-js` cell watches)
;; so they update automatically when the value arrives.
(def static-asset-domain (cell nil))

(defonce
  ^{:doc "A single, global instance of the Ironclad class defined in
          ui/js/ironclad.js."}
  ironclad
  (let [[site-access-id group-id]
        (if (or (= :aws-prod GEIR_ENVIRONMENT)
                (= :gcp-prod GEIR_ENVIRONMENT))
          ["e881bdfc-9973-470a-85df-6340ac479afb" "group-s1y7h6rak"]
          ["d75b9032-fe36-408e-8db2-489a02daf46f" "group-sjdk8nrx5"])]
    (delay (new js/Ironclad site-access-id group-id))))

(def body-click (cell 0))
(.on (jquerying "html,document") "click" #(swap! body-click inc))

(defn click-toggle-cell [& [c]]
  (with-let [c (or c (cell nil))]
    (add-watch body-click (gensym) #(reset! c nil))))

(def menu-open (click-toggle-cell))
(def modal-open (cell nil))

(defmethod do! :opens [el _ id]
  (el :class (cell= {:open (= id menu-open)})
      :click #(do (.stopPropagation %)
                  (swap! menu-open (fn [x] (and (not= x id) id))))))

(defc language-pack ::unknown)
(defc session-info ::unknown)
(defc user-and-network-info ::unknown)

(defn update-user-and-network-info
  [un-info {:keys [feature-flags custom-fields user]}]
  (-> un-info
      (assoc :feature-flags feature-flags
             :custom-fields custom-fields)
      (update :user #(if (= ::unknown %)
                       user
                       (merge % user)))))

(defn merge-if-populated!
  "Given a cell and a map of new data to merge in,
   swaps the value of the cell if that is not equal to
   the :ui.global-state/unknown placeholder value used
   for the initial states of various cells.
   Otherwise replaces the value with the new data."
  [input-cell new-data]
  (util/swap-if! input-cell
                 #(not= % ::unknown)
                 #(merge % new-data)
                 (constantly new-data)))

(defc= ping-response
  (cond->
   ;; This is the initial, un-initialized state. As various bits of information
   ;; are populated in other cells, we merge them into this map.
   {:user       ::unknown
    :session-id nil}

    (not= ::unknown language-pack)
    (update
     :user
     #(if (= ::unknown %)
        language-pack
        (merge % language-pack)))

    (not= ::unknown session-info)
    (merge session-info)

    (not= ::unknown user-and-network-info)
    (update-user-and-network-info user-and-network-info)))

(def route-cell (h/route-cell))

(def route-id-action-map
  (formula-of [route-cell]
              (let [action-parts    (rest (str/split route-cell #"/"))
                    map-keys        (concat []
                                            (when (-> action-parts first util/->number) [:networkid])
                                            [:route :id :action])]
                (zipmap  map-keys action-parts))))

(defc new-flight-id
  "The ID of the new flight being edited before saving it for the first time. We
   use this to keep track of whether the flight the user is currently editing is
   a new flight that hasn't been saved yet in the UI.

   This is kludgy; ideally we wouldn't save the flight to the database until the
   user clicks Save to begin with, and creating a flight in the UI wouldn't be
   conflated with editing an existing flight that we're saying is \"new\",
   semantically.

   See comments in adzerk.api.geir/new-flight for long term improvement ideas."
  nil)

(comment
  ;;;;; Monitoring the evolution of `ping-response`
  (defn- drop-dictionary
    [m]
    (cond-> m
      (and (:user m)
           (not= (:user m) ::unknown))
      (update-in [:user :dictionary] (constantly "dictionary!"))))

  (do-watch
   ping-response
   (fn [old new]
     (let [[old-only new-only _in-both] (diff old new)]
       (pprint {:old-only (drop-dictionary old-only)
                :new-only (drop-dictionary new-only)}))))

  (comment))
